import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import { resolveUploadUrl } from "../../../../infrastructure/utils/resolveUploadUrl";
import {
  isImageFile,
  isPdfFile,
} from "../../shared/files/filePreviewUtils";
import FullscreenImageOverlay from "./FullscreenImageOverlay";

const RequestFilesTab = ({ files = [], fileBaseUrl = "" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [brokenImages, setBrokenImages] = useState(() => new Set());
  const [fullscreen, setFullscreen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  const resolvedList = useMemo(
    () =>
      files.map((f) => ({
        ...f,
        resolvedUrl: resolveUploadUrl(f.url, fileBaseUrl),
      })),
    [files, fileBaseUrl]
  );

  const imageFiles = useMemo(
    () =>
      resolvedList.filter(
        (f) => isImageFile(f.url) && !brokenImages.has(f.label)
      ),
    [resolvedList, brokenImages]
  );

  const otherFiles = useMemo(
    () =>
      resolvedList.filter(
        (f) => !isImageFile(f.url) || brokenImages.has(f.label)
      ),
    [resolvedList, brokenImages]
  );

  useEffect(() => {
    if (!imageFiles.length) {
      setSelectedLabel(null);
      return;
    }
    const stillValid = imageFiles.some((f) => f.label === selectedLabel);
    if (!stillValid) setSelectedLabel(imageFiles[0].label);
  }, [imageFiles, selectedLabel]);

  const selected = imageFiles.find((f) => f.label === selectedLabel);
  const thumbSize = isMobile ? 64 : 72;

  if (!files.length) {
    return <Alert severity="info">فایلی برای این درخواست ثبت نشده است.</Alert>;
  }

  return (
    <Box>
      {imageFiles.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `${thumbSize + 16}px minmax(0, 1fr)`,
            },
            gap: 1.5,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              gap: 1,
              overflowX: { xs: "auto", sm: "hidden" },
              overflowY: { xs: "hidden", sm: "auto" },
              maxHeight: { sm: 200 },
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            {imageFiles.map((file) => {
              const active = file.label === selectedLabel;
              return (
                <Paper
                  key={file.label}
                  variant="outlined"
                  onClick={() => {
                    setSelectedLabel(file.label);
                    setPreviewFailed(false);
                  }}
                  sx={{
                    flex: { xs: "0 0 auto", sm: "0 0 auto" },
                    width: thumbSize,
                    cursor: "pointer",
                    borderWidth: active ? 2 : 1,
                    borderColor: active ? "primary.main" : "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={file.resolvedUrl}
                    alt={file.label}
                    loading="lazy"
                    onError={() =>
                      setBrokenImages((prev) => new Set(prev).add(file.label))
                    }
                    sx={{
                      width: thumbSize,
                      height: thumbSize - 18,
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Typography
                    variant="caption"
                    noWrap
                    display="block"
                    px={0.5}
                    py={0.25}
                    sx={{ maxWidth: thumbSize, fontSize: "0.65rem" }}
                  >
                    {file.label}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              minHeight: { xs: 160, sm: 180 },
              maxHeight: { xs: 220, sm: 200 },
              display: "flex",
              flexDirection: "column",
              bgcolor: "grey.50",
            }}
          >
            {selected && !previewFailed ? (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5, flexShrink: 0 }}
                >
                  <Typography variant="caption" fontWeight={600} noWrap>
                    {selected.label}
                  </Typography>
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title="تمام‌صفحه">
                      <IconButton
                        size="small"
                        aria-label="تمام‌صفحه"
                        onClick={() => setFullscreen(true)}
                      >
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="باز کردن در تب جدید">
                      <IconButton
                        size="small"
                        component="a"
                        href={selected.resolvedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="باز کردن"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    component="img"
                    src={selected.resolvedUrl}
                    alt={selected.label}
                    onError={() => setPreviewFailed(true)}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: { xs: 140, sm: 150 },
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1, py: 2 }}
                spacing={0.5}
              >
                <BrokenImageOutlinedIcon color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  پیش‌نمایش در دسترس نیست
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
      ) : null}

      {otherFiles.length > 0 ? (
        <Box sx={{ mt: imageFiles.length ? 1.5 : 0 }}>
          {imageFiles.length > 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              sx={{ mb: 0.75 }}
            >
              سایر مدارک
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 1,
            }}
          >
            {otherFiles.map((file) => {
              const Icon = isPdfFile(file.url)
                ? PictureAsPdfOutlinedIcon
                : DescriptionOutlinedIcon;
              return (
                <Paper
                  key={file.label}
                  variant="outlined"
                  sx={{
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 56,
                  }}
                >
                  <Icon color="action" fontSize="small" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" noWrap display="block">
                      {file.label}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }}
                        href={file.resolvedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      >
                        مشاهده
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ minWidth: 0, px: 0.5, fontSize: "0.7rem" }}
                        href={file.resolvedUrl}
                        download
                        endIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                      >
                        دانلود
                      </Button>
                    </Stack>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      ) : null}

      <FullscreenImageOverlay
        open={fullscreen && Boolean(selected?.resolvedUrl)}
        onClose={() => setFullscreen(false)}
        label={selected?.label}
        src={selected?.resolvedUrl}
      />
    </Box>
  );
};

RequestFilesTab.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
    })
  ),
  fileBaseUrl: PropTypes.string,
};

export default RequestFilesTab;
